import { Badge } from '@/components/ui-kit/badge';
import { Button } from '@/components/ui-kit/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import type { TemplateWithScreens } from '@/modules/templates/types/templates.types';
import { useLanguageContext } from '@/i18n/language-context';
import {
  useCreateTemplatePage,
  useGenerateLandingPage,
  useUpdateTemplatePage,
} from '@/modules/admin/hooks/use-template-pages';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Globe, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type {
  TemplatePage,
  TemplatePageInsertInput,
} from '../../types/landing-page.types';

const createLandingPageSchema = (t: (key: string, params?: Record<string, unknown>) => string) =>
  z.object({
    title: z
      .string()
      .min(1, t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_TITLEREQUIRED'))
      .max(200, t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_TITLEMAX')),
    description: z
      .string()
      .min(1, t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_DESCRIPTIONREQUIRED'))
      .max(2000, t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_DESCRIPTIONMAX')),
    htmlBody: z.string().min(1, t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_HTMLBODYREQUIRED')),
    ogTitle: z
      .string()
      .max(200, t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_OGTITLEMAX'))
      .optional()
      .nullable(),
    ogDescription: z
      .string()
      .max(500, t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_OGDESCRIPTIONMAX'))
      .optional()
      .nullable(),
    ogImage: z
      .string()
      .url(t('ADMIN_TEMPLATES_EDIT_LANDING_VALIDATION_OGIMAGEINVALID'))
      .optional()
      .nullable()
      .or(z.literal('')),
    keywords: z.array(z.string()).default([]).optional(),
    published: z.boolean().default(false),
  });

type LandingPageFormInput = z.input<ReturnType<typeof createLandingPageSchema>>;
type LandingPageFormData = z.output<ReturnType<typeof createLandingPageSchema>>;

interface LandingPageTabProps {
  template: TemplateWithScreens | null;
  landingPage: TemplatePage | null;
  landingPageExists: boolean;
  isLoading?: boolean;
}

export const LandingPageTab = ({
  template,
  landingPage,
  landingPageExists,
  isLoading = false,
}: LandingPageTabProps) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguageContext();

  const landingPageSchema = createLandingPageSchema(t);

  const [landingKeywordInput, setLandingKeywordInput] = useState('');
  // Abort the in-flight AI request if the component unmounts mid-generate
  // (e.g. tab switch). Recreated on each generate-click; the cleanup in
  // useEffect aborts whatever's current when the tab unmounts.
  const generateAbortRef = useRef<AbortController | null>(null);

  const createMutation = useCreateTemplatePage(template?.slug, currentLanguage);
  const updateMutation = useUpdateTemplatePage(template?.slug, currentLanguage);
  const generateMutation = useGenerateLandingPage();

  const isLandingPageSubmitting = createMutation.isPending || updateMutation.isPending;
  const isGeneratingLanding = generateMutation.isPending;

  useEffect(() => {
    return () => {
      generateAbortRef.current?.abort();
    };
  }, []);

  const landingPageForm = useForm<LandingPageFormInput, LandingPageFormData>({
    resolver: zodResolver(landingPageSchema),
    defaultValues: {
      title: '',
      description: '',
      htmlBody: '',
      ogTitle: null,
      ogDescription: null,
      ogImage: null,
      keywords: [],
      published: false,
    },
  });

  // Hydrate the form when an existing landing page loads (or refreshes).
  // Using the form's `reset` (rather than per-field setValue) clears
  // any half-edited state and matches react-hook-form's recommended
  // pattern for async default values.
  useEffect(() => {
    if (landingPage) {
      landingPageForm.reset({
        title: landingPage.title ?? '',
        description: landingPage.description ?? '',
        htmlBody: landingPage.htmlBody ?? '',
        ogTitle: landingPage.ogTitle ?? null,
        ogDescription: landingPage.ogDescription ?? null,
        ogImage: landingPage.ogImage ?? null,
        keywords: landingPage.keywords ?? [],
        published: landingPage.published ?? false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landingPage?.id]);

  const handleLandingPageSubmit = async (data: LandingPageFormData) => {
    if (!template?.slug) return;

    const input: TemplatePageInsertInput = {
      Slug: template.slug,
      Locale: currentLanguage,
      Title: data.title,
      Description: data.description,
      HtmlBody: data.htmlBody,
      OgTitle: data.ogTitle ?? null,
      OgDescription: data.ogDescription ?? null,
      OgImage: data.ogImage || null,
      Keywords: data.keywords ?? [],
      Published: data.published,
      // Keep the raw form body in Blocks too — the schema has both
      // HtmlBody and Blocks (serialized JSON) fields, and downstream
      // readers prefer Blocks. AI-generated edits set both fields to
      // the same stringified payload, so manual edits stay in sync.
      Blocks: data.htmlBody,
    };

    try {
      if (landingPageExists && landingPage?.id) {
        await updateMutation.mutateAsync({ itemId: landingPage.id, input });
      } else {
        await createMutation.mutateAsync(input);
      }
    } catch {
      // useErrorHandler in the hook already surfaced the toast.
    }
  };

  const addLandingKeyword = () => {
    const keyword = landingKeywordInput.trim().toLowerCase();
    const currentLandingKeywords = landingPageForm.getValues('keywords') ?? [];
    if (keyword && !currentLandingKeywords.includes(keyword)) {
      landingPageForm.setValue('keywords', [...currentLandingKeywords, keyword]);
      setLandingKeywordInput('');
    }
  };

  const removeLandingKeyword = (keyword: string) => {
    const currentLandingKeywords = landingPageForm.getValues('keywords') ?? [];
    landingPageForm.setValue(
      'keywords',
      currentLandingKeywords.filter((k) => k !== keyword)
    );
  };

  const handleGenerateLanding = async () => {
    if (!template) return;

    // Cancel any prior in-flight generate before starting a new one.
    generateAbortRef.current?.abort();
    const controller = new AbortController();
    generateAbortRef.current = controller;

    try {
      const result = await generateMutation.mutateAsync({
        template,
        locale: currentLanguage,
        signal: controller.signal,
      });

      // Mirror old client's field-by-field merge: AI values win when present,
      // otherwise fall back to the template's own title/description. Keep
      // `published` from whatever the user had set (don't reset it).
      const currentPublished = landingPageForm.getValues('published') ?? false;
      const blocksAsJson = JSON.stringify(result.blocks, null, 2);

      landingPageForm.reset({
        title: result.title || template.title,
        description: result.description || template.description || '',
        htmlBody: blocksAsJson,
        ogTitle: result.ogTitle || result.title || template.title,
        ogDescription:
          result.ogDescription || result.description || template.description || '',
        ogImage: result.ogImage || null,
        keywords: result.keywords ?? [],
        published: currentPublished,
      });
    } catch {
      // useErrorHandler in the hook already surfaced the toast (and
      // silently swallows AbortError).
    }
  };

  return (
    <form onSubmit={landingPageForm.handleSubmit(handleLandingPageSubmit)}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-[hsl(var(--fg))]">
            {t('ADMIN_TEMPLATES_EDIT_LANDING_TITLE')}
          </h2>
          <p className="text-sm text-globe-grey">{t('ADMIN_TEMPLATES_EDIT_LANDING_SUBTITLE')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            disabled={isGeneratingLanding || isLoading}
            onClick={handleGenerateLanding}
            className="gap-2"
            size="sm"
          >
            {isGeneratingLanding && <Loader2 className="h-4 w-4 animate-spin" />}
            {isGeneratingLanding
              ? t('ADMIN_TEMPLATES_EDIT_LANDING_ACTIONS_GENERATING')
              : t('ADMIN_TEMPLATES_EDIT_LANDING_ACTIONS_GENERATE')}
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      {!landingPageExists && (
        <div className="mb-6 p-4 bg-selise-blue/5 border border-selise-blue/20 rounded-lg">
          <div className="flex items-start gap-3">
            <Globe className="h-5 w-5 text-selise-blue mt-0.5" />
            <div>
              <p className="font-medium text-[hsl(var(--fg))]">
                {t('ADMIN_TEMPLATES_EDIT_LANDING_EMPTY_TITLE')}
              </p>
              <p className="text-sm text-globe-grey mt-1">
                {t('ADMIN_TEMPLATES_EDIT_LANDING_EMPTY_DESCRIPTION', {
                  url: `/templates/${template?.slug}`,
                })}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Page Metadata Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_TITLE')}</CardTitle>
              <CardDescription>
                {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_DESCRIPTION')}
              </CardDescription>
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="size-4 rounded border-border text-selise-blue focus:ring-selise-blue"
                checked={landingPageForm.watch('published')}
                onChange={(e) => landingPageForm.setValue('published', e.target.checked)}
              />
              <span className="text-sm font-medium text-[hsl(var(--fg))] flex items-center gap-1">
                {landingPageForm.watch('published') ? (
                  <>
                    <Eye className="h-4 w-4 text-lime-green" />
                    {t('ADMIN_TEMPLATES_EDIT_LANDING_STATUS_PUBLISHED')}
                  </>
                ) : (
                  <>
                    <EyeOff className="h-4 w-4 text-globe-grey" />
                    {t('ADMIN_TEMPLATES_EDIT_LANDING_STATUS_DRAFT')}
                  </>
                )}
              </span>
            </label>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Title and Description */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="landing-title">
                {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_PAGETITLELABEL')}
              </Label>
              <Input
                id="landing-title"
                placeholder={t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_PAGETITLEPLACEHOLDER')}
                {...landingPageForm.register('title')}
              />
              {landingPageForm.formState.errors.title && (
                <p className="text-sm text-destructive">
                  {landingPageForm.formState.errors.title.message}
                </p>
              )}
              <p className="text-sm text-globe-grey">
                {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_PAGETITLEHELP')}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="landing-og-title">
                {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGTITLELABEL')}
              </Label>
              <Input
                id="landing-og-title"
                placeholder={t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGTITLEPLACEHOLDER')}
                {...landingPageForm.register('ogTitle')}
              />
              <p className="text-sm text-globe-grey">
                {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGTITLEHELP')}
              </p>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="landing-description">
              {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_METADESCRIPTIONLABEL')}
            </Label>
            <textarea
              id="landing-description"
              placeholder={t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_METADESCRIPTIONPLACEHOLDER')}
              className="flex min-h-20 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              {...landingPageForm.register('description')}
            />
            {landingPageForm.formState.errors.description && (
              <p className="text-sm text-destructive">
                {landingPageForm.formState.errors.description.message}
              </p>
            )}
          </div>

          {/* OG Description */}
          <div className="space-y-2">
            <Label htmlFor="landing-og-description">
              {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGDESCRIPTIONLABEL')}
            </Label>
            <textarea
              id="landing-og-description"
              placeholder={t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGDESCRIPTIONPLACEHOLDER')}
              className="flex min-h-16 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              {...landingPageForm.register('ogDescription')}
            />
            <p className="text-sm text-globe-grey">
              {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGDESCRIPTIONHELP')}
            </p>
          </div>

          {/* OG Image */}
          <div className="space-y-2">
            <Label htmlFor="landing-og-image">
              {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGIMAGELABEL')}
            </Label>
            <div className="flex gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-muted">
                <ImageIcon className="h-5 w-5 text-globe-grey" />
              </div>
              <Input
                id="landing-og-image"
                placeholder={t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGIMAGEPLACEHOLDER')}
                {...landingPageForm.register('ogImage')}
                className="flex-1"
              />
            </div>
            {landingPageForm.formState.errors.ogImage && (
              <p className="text-sm text-destructive">
                {landingPageForm.formState.errors.ogImage.message}
              </p>
            )}
            <p className="text-sm text-globe-grey">
              {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_OGIMAGEHELP')}
            </p>
          </div>

          {/* Keywords */}
          <div className="space-y-2">
            <Label>{t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_KEYWORDSLABEL')}</Label>
            <div className="flex gap-2">
              <Input
                placeholder={t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_KEYWORDSPLACEHOLDER')}
                value={landingKeywordInput}
                onChange={(e) => setLandingKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addLandingKeyword();
                  }
                }}
              />
              <Button type="button" variant="outline" onClick={addLandingKeyword}>
                {t('ADMIN_TEMPLATES_EDIT_LANDING_METADATA_KEYWORDSADD')}
              </Button>
            </div>
            {(landingPageForm.watch('keywords') || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {(landingPageForm.watch('keywords') || []).map((keyword) => (
                  <Badge key={keyword} variant="secondary" className="gap-1 pr-1">
                    {keyword}
                    <button
                      type="button"
                      onClick={() => removeLandingKeyword(keyword)}
                      className="ml-1 rounded-full hover:bg-destructive/20"
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* HTML Body Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t('ADMIN_TEMPLATES_EDIT_LANDING_CONTENT_TITLE')}</CardTitle>
          <CardDescription>{t('ADMIN_TEMPLATES_EDIT_LANDING_CONTENT_DESCRIPTION')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="landing-html-body">
                {t('ADMIN_TEMPLATES_EDIT_LANDING_CONTENT_BODYLABEL')}
              </Label>
              {landingPageForm.watch('htmlBody') && (
                <span className="text-sm text-globe-grey">
                  {t('ADMIN_TEMPLATES_EDIT_LANDING_CONTENT_CHARACTERS', {
                    count: landingPageForm.watch('htmlBody')?.length ?? 0,
                  })}
                </span>
              )}
            </div>
            <textarea
              id="landing-html-body"
              placeholder={`<section class="py-16">
  <div class="container mx-auto px-4">
    <h1 class="text-4xl font-bold mb-4">Your Template Title</h1>
    <p class="text-lg text-muted-foreground">
      Describe what this template does and why users should use it.
    </p>
  </div>
</section>`}
              className="flex min-h-96 w-full rounded-lg border border-input bg-transparent px-3 py-2 text-sm font-mono shadow-sm placeholder:text-globe-grey focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              {...landingPageForm.register('htmlBody')}
            />
            {landingPageForm.formState.errors.htmlBody && (
              <p className="text-sm text-destructive">
                {landingPageForm.formState.errors.htmlBody.message}
              </p>
            )}
          </div>

          <div className="p-3 bg-muted/30 rounded-lg border border-border">
            <p className="text-sm text-globe-grey">
              <strong>{t('ADMIN_TEMPLATES_EDIT_LANDING_CONTENT_TIPSLABEL')}</strong>{' '}
              {t('ADMIN_TEMPLATES_EDIT_LANDING_CONTENT_TIPSBODY')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="md:flex-row flex flex-col gap-3 items-center justify-between">
        <div className="text-sm text-globe-grey">
          {landingPageExists ? (
            <span>
              {t('ADMIN_TEMPLATES_EDIT_LANDING_LASTUPDATED', {
                date: landingPage?.updatedAt
                  ? new Date(landingPage.updatedAt).toLocaleString()
                  : t('ADMIN_TEMPLATES_EDIT_LANDING_UNKNOWNDATE'),
              })}
            </span>
          ) : (
            <span>{t('ADMIN_TEMPLATES_EDIT_LANDING_CREATEHINT')}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {landingPageExists && (
            <Button size="sm" variant="outline" asChild>
              <a href={`/templates/${template?.slug}`} target="_blank" rel="noopener noreferrer">
                <Eye className="h-4 w-4 mr-2" />
                {t('ADMIN_TEMPLATES_EDIT_LANDING_ACTIONS_PREVIEW')}
              </a>
            </Button>
          )}
          <Button size="sm" type="submit" disabled={isLandingPageSubmitting}>
            {isLandingPageSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {landingPageExists
              ? t('ADMIN_TEMPLATES_EDIT_LANDING_ACTIONS_SAVECHANGES')
              : t('ADMIN_TEMPLATES_EDIT_LANDING_ACTIONS_CREATE')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default LandingPageTab;
